import {
  EyeIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  NeighbourhoodPerk,
  useDeleteNeighbourhoodPerk,
  useGetNeighbourhoodPerks,
} from '@/api/neighbourhood-perks'
import { TabCard } from '@/components/layout/tab-card'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { Select, SelectOption } from '@/components/ui/select'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { popupFactory, toastFactory } from '@/factories'
import { useFilters, useSearchDraft } from '@/hooks'
import { Building } from '@/reducers/types'
import { getApiErrorMessage } from '@/utils/error'

import { paginateItems } from '../../../../shared/person-table.utils'
import { useDetailsDeepLink } from '../../../../shared/use-details-deep-link'
import { getPlaceName } from '../../../places.utils'
import { PerkFormModal } from './perk-form-modal'
import { PerkType } from './perk-form-modal.constants'
import { PerkViewModal } from './perk-view-modal'
import { getPerkColumns } from './perks.columns'
import { PERKS_TABLE_ID, perksTableInitialFilters } from './perks.constants'
import { PerkSortKey } from './perks.sort-keys'
import * as S from './perks.styled'
import { filterPerks, getPerkCategoryOptions, searchPerks, sortPerks } from './perks.utils'

interface PerksTabProps {
  // Note: the tab renders (labels, filters, table) before the building has loaded, so only the id is required
  building?: Building
  buildingId: number
}

export const PerksTab = ({ building, buildingId }: PerksTabProps) => {
  const { t } = useTranslation(['organisation', 'neighborhood-perks', 'common'])
  const { data, error, isLoading } = useGetNeighbourhoodPerks(buildingId)
  const { mutateAsync: deletePerk } = useDeleteNeighbourhoodPerk()

  const tableFilters = useFilters({ initialState: perksTableInitialFilters })
  const { category, limit, search, skip, sortBy, sortOrder, type } = tableFilters.filters

  const [searchDraft, handleSearchChange] = useSearchDraft(search, (value) =>
    tableFilters.setFilter('search', value),
  )

  const categoryOptions = useMemo<SelectOption<string>[]>(
    () => getPerkCategoryOptions((key) => t(`neighborhood-perks:categories.${key}`)),
    [t],
  )

  const typeOptions = useMemo<SelectOption<PerkType>[]>(
    () => [
      { label: t('organisation:places.perks.types.promoCode'), value: PerkType.link },
      { label: t('organisation:places.perks.types.website'), value: PerkType.url },
    ],
    [t],
  )

  const filteredPerks = useMemo(
    () =>
      sortPerks(searchPerks(filterPerks(data ?? [], category, type), search), sortBy, sortOrder),
    [data, category, type, search, sortBy, sortOrder],
  )

  const perks = useMemo(
    () => paginateItems(filteredPerks, skip, limit),
    [filteredPerks, skip, limit],
  )

  const handleAdd = () => popupFactory.open(PerkFormModal, { buildingId })

  const openDetails = useDetailsDeepLink(PerkViewModal, (perkId: number) => ({
    buildingId,
    perkId,
  }))

  const handleView = (perk: NeighbourhoodPerk) => openDetails(perk.id)

  const handleEdit = (perk: NeighbourhoodPerk) =>
    popupFactory.open(PerkFormModal, { buildingId, perk })

  const handleDelete = (perk: NeighbourhoodPerk) =>
    popupFactory.open(ConfirmationPopup, {
      children: (
        <Trans
          components={[<strong key="perk" />, <strong key="building" />]}
          i18nKey="organisation:places.perks.deleteConfirm.body"
          t={t}
          values={{ buildingName: building ? getPlaceName(building) : '', name: perk.name }}
        />
      ),
      confirmIcon: TrashIcon,
      confirmText: t('organisation:places.perks.deleteConfirm.confirm'),
      onConfirm: async () => {
        try {
          await deletePerk(perk.id)
          toastFactory.success(t('organisation:places.perks.toasts.deleted'))
        } catch (deleteError) {
          toastFactory.error(getApiErrorMessage(deleteError))
        }
      },
      title: t('organisation:places.perks.deleteConfirm.title'),
      variant: 'danger',
    })

  const handleSort = (nextSortBy: string | null, nextSortOrder: SortOrder | null) =>
    tableFilters.setFilters({
      sortBy: (nextSortBy as PerkSortKey) ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })

  const columns = useMemo(() => getPerkColumns(t), [t])

  const rowActions: RowAction<NeighbourhoodPerk>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('organisation:places.perks.rowActions.view'),
        onClick: handleView,
        primary: true,
      },
      {
        icon: PencilSimpleIcon,
        label: t('common:actions.edit'),
        onClick: handleEdit,
      },
      {
        icon: TrashIcon,
        label: t('common:actions.delete'),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [t],
  )

  return (
    <TabCard
      fill
      actions={
        <Button size="small" onClick={handleAdd}>
          {t('organisation:places.perks.add')}
          <Icon icon={PlusIcon} />
        </Button>
      }
      title={t('organisation:places.perks.tabTitle')}
    >
      <TableGroup
        noHorizontalPadding
        noShadow
        columns={columns}
        data={perks}
        emptyNoFilters={{
          description: t('organisation:places.perks.empty.description'),
          title: t('organisation:places.perks.empty.title'),
        }}
        emptyWithFilters={{
          description: t('organisation:places.perks.empty.withFilters.description'),
          title: t('organisation:places.perks.empty.withFilters.title'),
        }}
        error={error}
        filters={
          <>
            <S.CategoryFilter>
              <Select
                searchable
                type="multi"
                options={categoryOptions}
                placeholder={t('organisation:places.perks.filters.category.placeholder')}
                size="small"
                value={category ?? []}
                onChange={(values) =>
                  tableFilters.setFilter('category', values.length ? values : undefined)
                }
                onReset={() => tableFilters.setFilter('category', undefined)}
              />
            </S.CategoryFilter>
            <Select
              type="multi"
              options={typeOptions}
              placeholder={t('organisation:places.perks.filters.type.placeholder')}
              size="small"
              value={type ?? []}
              onChange={(values) =>
                tableFilters.setFilter('type', values.length ? values : undefined)
              }
              onReset={() => tableFilters.setFilter('type', undefined)}
            />
          </>
        }
        getRowKey={(perk) => perk.id}
        id={PERKS_TABLE_ID}
        loading={isLoading}
        navigation={{
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              name="search"
              placeholder={t('organisation:places.perks.filters.search.placeholder')}
              size="small"
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          ),
        }}
        pagination={{ paginationFilters: tableFilters, total: filteredPerks.length }}
        rowActions={rowActions}
        sortBy={sortBy ?? null}
        sortOrder={sortOrder ?? null}
        tableFilters={tableFilters}
        onSort={handleSort}
      />
    </TabCard>
  )
}
